import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAddAdmin, useAdmins, useMe, useRemoveAdmin } from '@/lib/queries';
import { useCan } from '@/lib/use-can';
import { ApiError } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export function AdminsPage() {
  const can = useCan();
  const admins = useAdmins();
  const me = useMe();
  const addAdmin = useAddAdmin();
  const removeAdmin = useRemoveAdmin();
  const [open, setOpen] = useState(false);
  const [discordId, setDiscordId] = useState('');
  const [role, setRole] = useState<'admin' | 'support'>('admin');

  if (!can('settings.admins')) {
    return <p className="text-muted-foreground">You don't have access to this page.</p>;
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    try {
      await addAdmin.mutateAsync({ discordId, role });
      toast.success(`Admin ${discordId} added`);
      setOpen(false);
      setDiscordId('');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error';
      toast.error(msg);
    }
  }

  async function handleRemove(id: string) {
    if (!confirm(`Remove ${id} from the whitelist?`)) return;
    try {
      await removeAdmin.mutateAsync(id);
      toast.success('Removed');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error';
      toast.error(msg);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Admins</h1>
          <p className="text-muted-foreground">Discord IDs with access to this panel</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add admin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleAdd}>
              <DialogHeader>
                <DialogTitle>New admin</DialogTitle>
                <DialogDescription>
                  Discord ID — 17-20 digits (Developer Mode → right-click user → Copy ID)
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="discordId">Discord ID</Label>
                  <Input
                    id="discordId"
                    inputMode="numeric"
                    pattern="\d{17,20}"
                    required
                    value={discordId}
                    onChange={(e) => setDiscordId(e.target.value)}
                    placeholder="123456789012345678"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value as 'admin' | 'support')}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="admin">Admin — full access except managing other admins</option>
                    <option value="support">Support — view + close tickets, user notes</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={addAdmin.isPending}>
                  Add
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Whitelist</CardTitle>
          <CardDescription>
            Only these IDs can sign into the panel via Discord OAuth
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Discord ID</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Added by</TableHead>
                <TableHead>When</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {admins.data?.map((a) => (
                <TableRow key={a.discordId}>
                  <TableCell className="font-mono">{a.discordId}</TableCell>
                  <TableCell>
                    <Badge variant={a.role === 'owner' ? 'default' : 'secondary'}>{a.role}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {a.addedByDiscordId ?? '—'}
                  </TableCell>
                  <TableCell>{formatDate(a.addedAt)}</TableCell>
                  <TableCell>
                    {a.role !== 'owner' && a.discordId !== me.data?.discordId && (
                      <Button variant="ghost" size="icon" onClick={() => handleRemove(a.discordId)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
